"use client";

import { useFormContext } from "react-hook-form";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useI18n } from "@/lib/i18n/hook";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { HelpCircle } from "lucide-react";
import { useCurrency as useCurrencyContext } from "../contexts/currency-context";

const currencies = [
  { code: "USD", symbol: "$", translationKey: "common.currencies.usd" },
  { code: "JPY", symbol: "¥", translationKey: "common.currencies.jpy" },
  { code: "EUR", symbol: "€", translationKey: "common.currencies.eur" },
  { code: "GBP", symbol: "£", translationKey: "common.currencies.gbp" },
  { code: "AUD", symbol: "A$", translationKey: "common.currencies.aud" },
  { code: "CAD", symbol: "C$", translationKey: "common.currencies.cad" },
  { code: "CHF", symbol: "Fr", translationKey: "common.currencies.chf" },
  { code: "CNY", symbol: "¥", translationKey: "common.currencies.cny" },
  { code: "HKD", symbol: "HK$", translationKey: "common.currencies.hkd" },
  { code: "SGD", symbol: "S$", translationKey: "common.currencies.sgd" },
];

export const getCurrencySymbol = (code: string) => {
  return currencies.find((c) => c.code === code)?.symbol || "¥";
};

export function CurrencyUnit({ className }: { className?: string }) {
  const { symbol } = useCurrencyContext();
  return <span className={className}>{symbol}</span>;
}

export function CurrencySelect() {
  const { control } = useFormContext();
  const { t } = useI18n();
  const { currency, setCurrency, currencies } = useCurrencyContext();

  return (
    <FormField
      control={control}
      name="simulationSettings.currency"
      render={({ field }) => (
        <FormItem>
          <div className="flex items-center gap-2">
            <FormLabel>{t("lifePlan.simulationSettings.currency")}</FormLabel>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <HelpCircle className="h-4 w-4 text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t("lifePlan.simulationSettings.currencyHelp")}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <Select
            onValueChange={(value) => {
              field.onChange(value);
              setCurrency(value);
            }}
            defaultValue={field.value || currency}
          >
            <FormControl>
              <SelectTrigger>
                <SelectValue placeholder={t("common.select")} />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              {currencies.map((currency) => (
                <SelectItem key={currency.code} value={currency.code}>
                  {currency.symbol} {t(currency.translationKey)} (
                  {currency.code})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
